import TextField from '@mui/material/TextField';
import { Button, TableSortLabel } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import CircularWithValueLabel from '../../components/CircularWithValueLabel';
import axiosClient from '../../services/axios-client';
import { ASSETS_PATH } from '../../constants';
import AddHotspotsOnMap from './../../components/Maps/AddHotspotsOnMap';
import ManageHotspotsNearMe from './../../components/Maps/ManageHotspotsNearMe';
import { uploadAsset } from '../../utilities/assetUtils';
import { IMapData, ISpace, ILocation, IHotspot2D, IHotspotNearMe } from '../../models/Map';


const Map = () => {

    const [mapPop, setMapPop] = useState<boolean>(false);
    const { map_id } = useParams<{ map_id: string }>();
    const [mapData, setMapData] = useState<Partial<IMapData>>({});
    const [spaces, setSpaces] = useState<ISpace[]>([]);
    const [locations, setLocations] = useState<ILocation[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: 'group' | 'type'; direction: 'asc' | 'desc' } | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [activeHotspot, setActiveHotspot] = useState<IHotspot2D | null>(null);
    const [activeHotspotIndex, setActiveHotspotIndex] = useState<number | null>(null);
    const [nearMeHotspotsPop, setNearMeHotspotsPop] = useState<boolean>(false);

    const onCloseMapPop = () => {
        setActiveHotspot(null);
        setActiveHotspotIndex(null);
    }


    const onUpdateAndClosePop = (hotspot: IHotspot2D): void => {

        setMapData((prev: Partial<IMapData>) => {

            let hotspots: IHotspot2D[] = [...(prev.map_hotspots_2d || [])];
            if (activeHotspotIndex !== null) {
                hotspots[activeHotspotIndex] = hotspot;
            }

            return {
                ...prev, map_hotspots_2d: hotspots
            }
        })

        setActiveHotspot(null);
        setActiveHotspotIndex(null);
    }

    const onOpenMapPop = (hotspot: IHotspot2D, index: number): void => {
        setActiveHotspot(hotspot);
        setActiveHotspotIndex(index)
    }

    const onDeleteHotspot = (index: number): void => {
        let hotspots: IHotspot2D[] = [...(mapData.map_hotspots_2d || [])];

        hotspots.splice(index, 1);

        setMapData(prev => {
            return { ...prev, map_hotspots_2d: hotspots }
        })

    }

    const onChangeUploadMapImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
        uploadAsset(e, (fileName: string) => {
            setMapData(d => {
                return { ...d, map_image_2d: fileName }
            })
        }, setUploading, setProgress);
    }

    const onRemoveImage = () => {
        setMapData(d => {
            return { ...d, map_image_2d: "" }
        })
    }

    const onSaveMapData = () => {

        let payload = {
            ...mapData,
            map_hotspots_2d: mapData.map_hotspots_2d || [],
            map_hotspots_near_me: mapData.map_hotspots_near_me || []
        }


        fetch(`${process.env.REACT_APP_API_URL}/map`, {
            method: "PUT",
            body: JSON.stringify(payload),
            headers: {
                "Content-Type": "application/json",
            }
        })
            .then((res) => res.json())
            .then(json => {
                alert(json.message);
            }).catch(err => {
                console.log(err)
            })
    }



    const onChangeMapName = (e: React.ChangeEvent<HTMLInputElement>): void => {

        let name = e.target.value;

        setMapData(d => {
            return { ...d, map_name: name }
        })



    }

    const getHotspotDisplayName = (hotspot: IHotspot2D): string => {

        console.log(hotspot.space_id)

        if (hotspot.space_id && hotspot.space_id !== 0) {
            const space = spaces.find(s => s.space_id === hotspot.space_id);
            if (space) {
                return space.space_name;
            }
        } else if (hotspot.location_id && hotspot.location_id !== 0) {
            const location = locations.find(l => l.location_id === hotspot.location_id);
            if (location) {
                return location.location_name;
            }
        }
        return "Untitled";
    };


    const onAddRoute = (): void => {
        const newRoute: IHotspot2D = { location_id: 0, d_location_id: 0, x: 0, y: 0, dx: 0, dy: 0, space_id: 0, d_space_id: 0, route: '' };

        const hotspots: IHotspot2D[] = [...(mapData.map_hotspots_2d || [])];

        hotspots.push(newRoute);

        setMapData(prev => {
            return { ...prev, map_hotspots_2d: hotspots }
        });

        onOpenMapPop(newRoute, hotspots.length - 1);

    }

    const onOpenNearMeHotspotsPop = () => {
        setNearMeHotspotsPop(true);
    }

    const onCloseNearMeHotspotsPop = () => {
        setNearMeHotspotsPop(false);
    }

    const onUpdateNearMeHotspots = (hotspots: IHotspotNearMe[]): void => {
        setMapData(prev => {
            return { ...prev, map_hotspots_near_me: hotspots }
        });
    }

    const sortedNearMeHotspots = (() => {
        if (!mapData.map_hotspots_near_me) return [];
        let sortableItems = [...mapData.map_hotspots_near_me];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aValue, bValue;
                if (sortConfig.key === 'group') {
                    aValue = a.group || 'Default';
                    bValue = b.group || 'Default';
                } else { // type
                    aValue = a.space_id ? 'space' : 'location';
                    bValue = b.space_id ? 'space' : 'location';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    })();

    const requestSort = (key: 'group' | 'type') => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/map/${map_id}`, { method: 'GET' })
            .then((res) => res.json())
            .then((json) => {
                console.log(json);
                const map = json.map;
                // Parse JSON fields if they are strings
                if (typeof map.map_hotspots_2d === 'string') {
                    map.map_hotspots_2d = JSON.parse(map.map_hotspots_2d || '[]');
                }
                if (typeof map.map_hotspots_near_me === 'string') {
                    map.map_hotspots_near_me = JSON.parse(map.map_hotspots_near_me || '[]');
                }
                setMapData(map);
            });

        fetch(`${process.env.REACT_APP_API_URL}/space`, { method: 'GET' })
            .then((res) => res.json())
            .then((json) => {
                setSpaces(json.spaces);
            });

        fetch(`${process.env.REACT_APP_API_URL}/locations`, { method: 'GET' })
            .then((res) => res.json())
            .then((json) => {
                setLocations(json.locations);
            });
    }, [])

    return (
        <>
            <div className="content-body">
                <form>
                    <TextField className='textfield w-full' value={mapData.map_name} onChange={onChangeMapName} />
                    <br />
                    <br />
                    {mapData.map_image_2d && <>
                        <img src={`${ASSETS_PATH}/${mapData.map_image_2d}`} style={{ width: '400px' }} />
                        <br />
                        <br />
                    </>
                    }
                    {!mapData.map_image_2d && <Button
                        variant="contained"
                        component="label"
                        style={{ marginRight: "10px" }}
                    >
                        *Upload Image
                        <input
                            type="file"
                            hidden
                            onChange={onChangeUploadMapImage}
                        />
                    </Button>}

                    {mapData.map_image_2d &&
                        <>
                            <Button variant='contained' onClick={onRemoveImage}>Remove Image</Button>
                        </>}
                    <br />
                    <br />
                    <Button variant='contained' onClick={onAddRoute}>Add new route</Button>
                    <br />
                    <br />
                    <table className="table-auto border w-full">
                        <thead>
                            <tr>
                                <th className="font-bold p-2 border-b text-left w-2">No</th>
                                <th className="font-bold p-2 border-b text-left">Hotspot Name</th>
                                <th className="font-bold py-2 px-4 border-b text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>

                            {mapData.map_hotspots_2d && mapData.map_hotspots_2d.filter((h: IHotspot2D) => h.route).map((hotspot: IHotspot2D, index: number) => <tr key={index}>
                                <td className="p-2 border-b text-left">{index + 1}</td>
                                <td className="p-2 border-b text-left">{getHotspotDisplayName(hotspot)}</td>
                                <td className="py-2 px-4 border-b text-left">
                                    <Button onClick={() => { onOpenMapPop(hotspot, index) }}>Edit</Button>
                                    <Button onClick={() => { onDeleteHotspot(index) }}>Delete</Button>
                                </td>
                            </tr>)}

                        </tbody>
                    </table>

                    <hr />
                    <br />
                    <h2>Near Me Hotspots</h2>
                    <br />
                    <Button variant='contained' onClick={onOpenNearMeHotspotsPop}>Manage Near Me Hotspots</Button>
                    <br />
                    <br />
                    {mapData.map_hotspots_near_me && mapData.map_hotspots_near_me.length > 0 && (
                        <table className="table-auto border w-full">
                            <thead>
                                <tr>
                                    <th className="font-bold p-2 border-b text-left w-2">No</th>
                                    <th className="font-bold p-2 border-b text-left">Hotspot Name</th>
                                    <th className="font-bold p-2 border-b text-left">
                                        <TableSortLabel
                                            active={sortConfig?.key === 'type'}
                                            direction={sortConfig?.key === 'type' ? sortConfig.direction : 'asc'}
                                            onClick={() => requestSort('type')}
                                        >
                                            Type
                                        </TableSortLabel>
                                    </th>
                                    <th className="font-bold p-2 border-b text-left">
                                        <TableSortLabel
                                            active={sortConfig?.key === 'group'}
                                            direction={sortConfig?.key === 'group' ? sortConfig.direction : 'asc'}
                                            onClick={() => requestSort('group')}
                                        >
                                            Group
                                        </TableSortLabel>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedNearMeHotspots.map((hotspot, index) => {
                                    const isSpace = hotspot.space_id && hotspot.space_id !== 0;
                                    const item = isSpace
                                        ? spaces.find((s: ISpace) => s.space_id === hotspot.space_id)
                                        : locations.find((l: ILocation) => l.location_id === hotspot.location_id);
                                    const name = item ? (isSpace ? (item as ISpace).space_name : (item as ILocation).location_name) : 'Unknown';
                                    return (
                                        <tr key={index}>
                                            <td className="p-2 border-b text-left">{index + 1}</td>
                                            <td className="p-2 border-b text-left">{name}</td>
                                            <td className="p-2 border-b text-left">
                                                <span style={{ color: isSpace ? 'blue' : 'green' }}>
                                                    {isSpace ? 'Space' : 'Location'}
                                                </span>
                                            </td>
                                            <td className="p-2 border-b text-left">{hotspot.group || 'Default'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    <hr />
                    <br />

                    <Button variant='contained' onClick={onSaveMapData}>Save</Button>

                </form>
            </div>
            {activeHotspot &&
                <AddHotspotsOnMap
                    onCloseMapPop={onCloseMapPop}
                    mapData={mapData}
                    hotspot={activeHotspot}
                    spaces={spaces}
                    locations={locations}
                    onUpdateAndClosePop={onUpdateAndClosePop}
                />
            }
            {nearMeHotspotsPop && mapData.map_image_2d &&
                <ManageHotspotsNearMe
                    onClose={onCloseNearMeHotspotsPop}
                    mapData={mapData}
                    spaces={spaces}
                    locations={locations}
                    onUpdateHotspots={onUpdateNearMeHotspots}
                />
            }
            {uploading && <CircularWithValueLabel progress={progress} />}
        </>

    )
}

export default Map;